import React, { FC, useState, useRef, useMemo, useEffect, useCallback } from "react";
import { ColumnConfig, ColumnGroup, numberSort, SortDirections, TextAlignment, wafflesKeyColumnName } from "@app/shared/waffles";
import { Waffles } from "@app/shared/waffles/Waffles";
import InfoIndicator from "@app/shared/InfoIndicator";
import {
    JobPerformanceClientRanking,
    JobPerformanceClientRunDetail,
    JobPerformanceDashboardRequest,
} from "@app/operations/job-performance/jobPerformanceInterfaces";
import { JobPerformanceService } from "@app/operations/job-performance/jobPerformanceService";
import { JobPerformanceClientDetailPanel } from "@app/operations/job-performance/JobPerformanceClientDetailPanel";

const CLIENT_DETAIL_PAGE_SIZE = 10;

const TIME_TO_PUBLISH_TOOLTIP = 'Time from project run finishing to the publish button being clicked. Measures the manual response time — this approaches zero with self-service publishing.';

const TimeToPublishHeader = (
    <span style={{ alignItems: 'center', display: 'inline-flex', gap: '3px' }}>
        Time to Publish (Min)
        <InfoIndicator className="jp-section-header__icon" tooltipTitle={TIME_TO_PUBLISH_TOOLTIP} tooltipWidth="320px" />
    </span>
);

type ClientRankingRow = {
    [wafflesKeyColumnName]: string;
    avgEndToEnd:                        number | string | null;
    avgPubQueueWait:                    number | string | null;
    avgPubRunTime:                      number | string | null;
    avgQueueWait:                       number | string | null;
    avgRunTime:                         number | string | null;
    avgTimeToPublish:                   number | string | null;
    bestRunTime:                        number | string | null;
    client:                             string | null;
    hasZipSummaries:                    boolean | null;
    isDualMembership:                   boolean | null;
    outlierThreshold:                   number | string | null;
    outliers:                           number | null;
    outliersChip:                       React.ReactNode;
    project:                            string | null;
    projectId:                          number | null;
    quarantined:                        number | null;
    quarantinedChip:                    React.ReactNode;
    rank:                               number | null;
    showAffiliationsInsights:           boolean | null;
    showNetworkIntegrityDashboard:      boolean | null;
    showNextClosestNetworkProviders:    boolean | null;
    showPciData:                        boolean | null;
    totalRuns:                          number | null;
    useSnowflakeProviderSetsForAnalysis: boolean | null;
    worstRunTime:                       number | string | null;
};

type Props = {
    data:    JobPerformanceClientRanking[];
    filters: JobPerformanceDashboardRequest;
    loading: boolean;
};

const columnGroups: Array<ColumnGroup<ClientRankingRow>> = [
    { alignment: TextAlignment.Center, children: ['rank', 'client'],                                                                                                                                          key: '',                                          testId: 'ranking-group-identity' },
    { alignment: TextAlignment.Center, children: ['avgQueueWait', 'avgRunTime', 'avgTimeToPublish', 'avgPubQueueWait', 'avgPubRunTime', 'avgEndToEnd', 'bestRunTime', 'worstRunTime', 'outlierThreshold'],   key: 'Avg Timing Breakdown — JobType 1002 (Min)', testId: 'ranking-group-timing' },
    { alignment: TextAlignment.Center, children: ['totalRuns', 'outliersChip', 'quarantinedChip'],                                                                                                            key: 'Run Counts',                                testId: 'ranking-group-counts' },
];

export const JobPerformanceClientRankingTable: FC<Props> = ({ data, filters, loading }) => {
    const service     = useRef(new JobPerformanceService()).current;
    const fetchedKeys = useRef<Set<string>>(new Set());

    /* oxlint-disable sort-keys */
    const tableData = useMemo<ClientRankingRow[]>(() => data.map((r): ClientRankingRow => ({
        avgEndToEnd:      r.avgTotalEndToEndMinutes?.toFixed(1) ?? null,
        avgPubQueueWait:  r.avgPublishQueueWaitMinutes?.toFixed(1) ?? null,
        avgPubRunTime:    r.avgPublishRunTimeMinutes?.toFixed(1) ?? null,
        avgQueueWait:     r.avgQueueWaitMinutes?.toFixed(1) ?? null,
        avgRunTime:       r.avgProjectRunTimeMinutes?.toFixed(1) ?? null,
        avgTimeToPublish: r.avgTimeToPublishMinutes?.toFixed(1) ?? null,
        bestRunTime:      r.bestRunTimeMinutes?.toFixed(1) ?? null,
        client:           r.clientName,
        hasZipSummaries:  null,
        isDualMembership: null,
        outlierThreshold: r.outlierThresholdMinutes?.toFixed(1) ?? null,
        outliers:         r.outlierCount,
        outliersChip:     r.outlierCount > 0    ? <span className="jp-chip jp-chip--active">{r.outlierCount}</span>    : <span className="jp-chip jp-chip--muted">0</span>,
        project:          null,
        projectId:        null,
        quarantined:      r.quarantinedCount,
        quarantinedChip:  r.quarantinedCount > 0 ? <span className="jp-chip jp-chip--active">{r.quarantinedCount}</span> : <span className="jp-chip jp-chip--muted">0</span>,
        rank:             r.rank,
        showAffiliationsInsights: null,
        showNetworkIntegrityDashboard: null,
        showNextClosestNetworkProviders: null,
        showPciData: null,
        totalRuns:        r.totalProjectRuns,
        useSnowflakeProviderSetsForAnalysis: null,
        [wafflesKeyColumnName]: r.clientId.toString(),
        worstRunTime:     r.worstRunTimeMinutes?.toFixed(1) ?? null,
    })), [data]);
    /* oxlint-enable sort-keys */

    const [dataExpansions, setDataExpansions] = useState<Map<string, ClientRankingRow[]>>(
        () => new Map(data.map(r => [r.clientId.toString(), [] as ClientRankingRow[]]))
    );
    const [detailPagination, setDetailPagination] = useState<Map<string, { offset: number, totalCount: number }>>(new Map());
    const [loadingMoreDetail, setLoadingMoreDetail] = useState<Set<string>>(new Set());
    const loadingMoreKeys = useRef<Set<string>>(new Set());

    useEffect(() => {
        fetchedKeys.current.clear();
        setDataExpansions(new Map(data.map(r => [r.clientId.toString(), [] as ClientRankingRow[]])));
        setDetailPagination(new Map());
    }, [data]);

    /* oxlint-disable sort-keys */
    const mapDetailRows = (expansionKey: string, items: JobPerformanceClientRunDetail[]): ClientRankingRow[] =>
        items.map((d): ClientRankingRow => ({
            avgEndToEnd:      d.avgTotalEndToEndMinutes,
            avgPubQueueWait:  d.avgPublishQueueWaitMinutes,
            avgPubRunTime:    d.avgPublishRunTimeMinutes,
            avgQueueWait:     d.avgQueueWaitMinutes,
            avgRunTime:       d.avgRunTimeMinutes,
            avgTimeToPublish: d.avgTimeToPublishMinutes,
            bestRunTime:      d.bestRunTimeMinutes,
            client:           null,
            hasZipSummaries:  d.hasZipSummaries,
            isDualMembership: d.isDualMembership,
            outlierThreshold: null,
            outliers:         d.outlierCount,
            outliersChip:     d.outlierCount > 0    ? <span className="jp-chip jp-chip--active">{d.outlierCount}</span>    : <span className="jp-chip jp-chip--muted">0</span>,
            project:          d.projectName,
            projectId:        d.projectId,
            quarantined:      d.quarantinedCount,
            quarantinedChip:  d.quarantinedCount > 0 ? <span className="jp-chip jp-chip--active">{d.quarantinedCount}</span> : <span className="jp-chip jp-chip--muted">0</span>,
            rank:             null,
            showAffiliationsInsights: d.showAffiliationsInsights,
            showNetworkIntegrityDashboard: d.showNetworkIntegrityDashboard,
            showNextClosestNetworkProviders: d.showNextClosestNetworkProviders,
            showPciData: d.showPciData,
            totalRuns:        d.totalRuns,
            useSnowflakeProviderSetsForAnalysis: d.useSnowflakeProviderSetsForAnalysis,
            [wafflesKeyColumnName]: `${expansionKey}-${d.projectRunConfigurationId}-${d.dateQueued}`,
            worstRunTime:     d.worstRunTimeMinutes,
        }));
    /* oxlint-enable sort-keys */

    const handleExpansionToggle = async (expansionKey: string) => {
        if (fetchedKeys.current.has(expansionKey)) return;

        try {
            const clientId = parseInt(expansionKey);
            const detail   = await service.getClientRunDetail(clientId, { ...filters, offset: 0, pageSize: CLIENT_DETAIL_PAGE_SIZE });
            fetchedKeys.current.add(expansionKey);
            setDetailPagination(prev => new Map(prev).set(expansionKey, { offset: detail.items.length, totalCount: detail.totalCount }));
            if (!detail.items.length) return;

            setDataExpansions(prev => new Map(prev).set(expansionKey, mapDetailRows(expansionKey, detail.items)));
        } catch {
            /* oxlint-disable sort-keys */
            const errorRow: ClientRankingRow = {
                avgEndToEnd: null, avgPubQueueWait: null, avgPubRunTime: null, avgQueueWait: null,
                avgRunTime: null, avgTimeToPublish: null, bestRunTime: null,
                client: 'Failed to load project details. Please try again.',
                hasZipSummaries: null,
                isDualMembership: null,
                outlierThreshold: null, outliers: null, outliersChip: null, project: null,
                projectId: null, quarantined: null, quarantinedChip: null, rank: null,
                showAffiliationsInsights: null, showNetworkIntegrityDashboard: null,
                showNextClosestNetworkProviders: null, showPciData: null,
                totalRuns: null,
                useSnowflakeProviderSetsForAnalysis: null,
                [wafflesKeyColumnName]: `${expansionKey}-error`,
                worstRunTime: null,
            };
            /* oxlint-enable sort-keys */
            setDataExpansions(prev => new Map(prev).set(expansionKey, [errorRow]));
        }
    };

    const handleLoadMoreDetail = async (expansionKey: string) => {
        const pagination = detailPagination.get(expansionKey);
        if (!pagination || loadingMoreKeys.current.has(expansionKey)) return;

        loadingMoreKeys.current.add(expansionKey);
        setLoadingMoreDetail(prev => new Set(prev).add(expansionKey));
        try {
            const clientId = parseInt(expansionKey);
            const detail   = await service.getClientRunDetail(clientId, { ...filters, offset: pagination.offset, pageSize: CLIENT_DETAIL_PAGE_SIZE });
            const newRows  = mapDetailRows(expansionKey, detail.items);
            setDataExpansions(prev => new Map(prev).set(expansionKey, [...(prev.get(expansionKey) ?? []), ...newRows]));
            setDetailPagination(prev => new Map(prev).set(expansionKey, { offset: pagination.offset + detail.items.length, totalCount: detail.totalCount }));
        } finally {
            loadingMoreKeys.current.delete(expansionKey);
            setLoadingMoreDetail(prev => {
                const next = new Set(prev);
                next.delete(expansionKey);
                return next;
            });
        }
    };

    const handleRankingSort = useCallback((segment: ClientRankingRow[], column: keyof ClientRankingRow, direction: SortDirections): ClientRankingRow[] => {
        const mult = direction === SortDirections.Asc ? 1 : -1;
        if (column === 'avgEndToEnd' || column === 'avgPubQueueWait' || column === 'avgPubRunTime' || column === 'avgQueueWait' || column === 'avgRunTime' || column === 'avgTimeToPublish' || column === 'bestRunTime' || column === 'worstRunTime' || column === 'outlierThreshold') {
            return numberSort(segment, column, mult);
        }
        if (column === 'rank' || column === 'totalRuns') {
            return [...segment].sort((a, b) => ((a[column] as number ?? 0) - (b[column] as number ?? 0)) * mult);
        }
        if (column === 'outliersChip' || column === 'outliers') {
            return [...segment].sort((a, b) => ((a.outliers ?? 0) - (b.outliers ?? 0)) * mult);
        }
        if (column === 'quarantinedChip' || column === 'quarantined') {
            return [...segment].sort((a, b) => ((a.quarantined ?? 0) - (b.quarantined ?? 0)) * mult);
        }
        return [...segment].sort((a, b) => (String(a[column] ?? '') > String(b[column] ?? '') ? 1 : -1) * mult);
    }, []);

    const renderClientPanel = useCallback((key: string): React.ReactNode => {
        const parentRow  = tableData.find(r => r[wafflesKeyColumnName] === key);
        const detailRows = dataExpansions.get(key) ?? [];
        const pagination = detailPagination.get(key);
        const hasMore    = (pagination?.offset ?? 0) < (pagination?.totalCount ?? 0);
        return (
            <JobPerformanceClientDetailPanel
                clientName={parentRow?.client ?? ''}
                hasMore={hasMore}
                loadingMore={loadingMoreDetail.has(key)}
                onLoadMore={() => handleLoadMoreDetail(key)}
                rows={detailRows}
            />
        );
    }, [tableData, dataExpansions, detailPagination, loadingMoreDetail]);

    const columnConfig: ColumnConfig = useMemo(() => ({
        avgEndToEnd:      { alignment: TextAlignment.Right, displayText: 'Total End-to-End Time (Min)', sizeContentToFit: true, sortOrder: 70,  testId: 'ranking-avg-end-to-end' },
        avgPubQueueWait:  { alignment: TextAlignment.Right, displayText: 'Pub Queue Wait (Min)',         sizeContentToFit: true, sortOrder: 60,  testId: 'ranking-avg-pub-queue-wait' },
        avgPubRunTime:    { alignment: TextAlignment.Right, displayText: 'Pub Run Time (Min)',            sizeContentToFit: true, sortOrder: 65,  testId: 'ranking-avg-pub-run-time' },
        avgQueueWait:     { alignment: TextAlignment.Right, displayText: 'Queue Wait Time (Min)',         sizeContentToFit: true, sortOrder: 30,  testId: 'ranking-avg-queue-wait' },
        avgRunTime:       { alignment: TextAlignment.Right, displayText: 'Project Run Time (Min)',        sizeContentToFit: true, sortOrder: 40,  testId: 'ranking-avg-run-time' },
        avgTimeToPublish: { alignment: TextAlignment.Right, displayElement: TimeToPublishHeader, displayText: 'Time to Publish (Min)', sizeContentToFit: true, sortOrder: 50,  testId: 'ranking-avg-time-to-publish' },
        bestRunTime:      { alignment: TextAlignment.Right, displayText: 'Best Run Time (Min)',           sizeContentToFit: true, sortOrder: 80,  testId: 'ranking-best-run-time' },
        client:           { displayText: 'Client',                                            sizeContentToFit: true, sortOrder: 20,  testId: 'ranking-client' },
        hasZipSummaries:  { displayText: 'Zip Summaries',   hidden: true,                     sizeContentToFit: true,                testId: 'ranking-has-zip-summaries' },
        isDualMembership: { displayText: 'Dual Membership', hidden: true,                     sizeContentToFit: true,                testId: 'ranking-is-dual-membership' },
        outliers:         { displayText: 'Outliers',  hidden: true,                           sizeContentToFit: true,                testId: 'ranking-outliers' },
        outliersChip:     { alignment: TextAlignment.Right, displayText: 'Outliers',                     sizeContentToFit: true, sortOrder: 110, testId: 'ranking-outliers-chip' },
        outlierThreshold: { alignment: TextAlignment.Right, displayText: 'Outlier Threshold',            sizeContentToFit: true, sortOrder: 90,  testId: 'ranking-outlier-threshold' },
        project:          { displayText: 'Project',   hidden: true,                           sizeContentToFit: true,                testId: 'ranking-project' },
        projectId:        { displayText: 'Project ID', hidden: true,                          sizeContentToFit: true,                testId: 'ranking-project-id' },
        quarantined:      { displayText: 'Quarantined', hidden: true,                         sizeContentToFit: true,                testId: 'ranking-quarantined' },
        quarantinedChip:  { alignment: TextAlignment.Right, displayText: 'Quarantined',                  sizeContentToFit: true, sortOrder: 120, testId: 'ranking-quarantined-chip' },
        rank:             { displayText: 'Rank',                                              sizeContentToFit: true, sortOrder: 10,  testId: 'ranking-rank' },
        showAffiliationsInsights:        { displayText: 'Affiliations Insights',       hidden: true, sizeContentToFit: true,          testId: 'ranking-show-affiliations-insights' },
        showNetworkIntegrityDashboard:   { displayText: 'Network Integrity',           hidden: true, sizeContentToFit: true,          testId: 'ranking-show-network-integrity-dashboard' },
        showNextClosestNetworkProviders: { displayText: 'Next Closest Network Providers', hidden: true, sizeContentToFit: true,       testId: 'ranking-show-next-closest-network-providers' },
        showPciData:      { displayText: 'Provider Claims Insights Data', hidden: true,        sizeContentToFit: true,                testId: 'ranking-show-pci-data' },
        totalRuns:        { alignment: TextAlignment.Right, displayText: 'Total Project Runs',            sizeContentToFit: true, sortOrder: 100, testId: 'ranking-total-runs' },
        useSnowflakeProviderSetsForAnalysis: { displayText: 'Snowflake Provider Sets', hidden: true,       sizeContentToFit: true,     testId: 'ranking-use-snowflake-provider-sets-for-analysis' },
        worstRunTime:     { alignment: TextAlignment.Right, displayText: 'Worst Run Time (Min)',          sizeContentToFit: true, sortOrder: 85,  testId: 'ranking-worst-run-time' },
    }), []);

    return (
        <Waffles<ClientRankingRow>
            columnGroups={columnGroups}
            columns={columnConfig}
            customSort={handleRankingSort}
            data={tableData}
            dataExpansions={dataExpansions}
            dataExpansionOnToggleClick={handleExpansionToggle}
            renderExpansionPanel={renderClientPanel}
            loading={loading}
            testId="job-performance__client-ranking-table"
        />
    );
};
